import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { ArrowUpRight, Ruler, ScanSearch, Layers, MapPin } from "lucide-react";
import { Page, Reveal, SectionTag, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import BlueprintSlider from "@/components/BlueprintSlider";
import { IMAGES, STATES, TERRITORIES, PROCESS_STEPS, MATTERPORT_TOUR_URL } from "@/lib/data";

const HeroVisual = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const imgX = useTransform(mx, [-0.5, 0.5], [12, -12]);
  const imgY = useTransform(my, [-0.5, 0.5], [10, -10]);
  const chipX = useTransform(mx, [-0.5, 0.5], [-18, 18]);
  const chipY = useTransform(my, [-0.5, 0.5], [-14, 14]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div onMouseMove={onMove} className="relative" data-testid="hero-visual">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        style={{ x: imgX, y: imgY }}
        className="relative"
      >
        <span className="absolute -left-3 -top-3 h-6 w-6 border-l-2 border-t-2 border-cad/70" />
        <span className="absolute -right-3 -top-3 h-6 w-6 border-r-2 border-t-2 border-cad/70" />
        <span className="absolute -bottom-3 -left-3 h-6 w-6 border-b-2 border-l-2 border-cad/70" />
        <span className="absolute -bottom-3 -right-3 h-6 w-6 border-b-2 border-r-2 border-cad/70" />
        <div className="overflow-hidden rounded-sm border border-white/10">
          <img
            src={IMAGES.hero}
            alt="Empty retail space measured for an as-built survey"
            className="h-64 w-full object-cover sm:h-[420px] lg:h-[520px]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-canvas/80 via-transparent to-canvas/20" />
        </div>
        <div className="absolute inset-x-6 bottom-6 flex items-end justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cad">
            <div className="mb-1 h-px w-24 bg-cad/60" />
            42'-08" / 13.00 M
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
            RETAIL · EXISTING CONDITIONS
          </div>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 1.1 }}
        style={{ x: chipX, y: chipY }}
        className="absolute -top-5 right-6 flex items-center gap-2 rounded-sm border border-cad/40 bg-surface/90 px-3 py-2 backdrop-blur-md"
      >
        <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-precision" />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cad">Scan active</span>
      </motion.div>
    </div>
  );
};

const BENTO = [
  {
    icon: Ruler,
    span: "md:col-span-7",
    title: "As-Built Surveys",
    body: "We field-measure your retail space and turn it into clean, accurate drawings your design team can build from — every dimension taken on site, by us.",
    meta: "DWG · PDF",
    image: IMAGES.blueprint,
    testid: "bento-as-built",
  },
  {
    icon: ScanSearch,
    span: "md:col-span-5",
    title: "Matterport 3D Scans",
    body: "A complete 3D digital model of your space. Walk through it from any computer or phone, check conditions, take measurements — no return trip.",
    meta: "3D TOUR · LINK",
    image: IMAGES.luxury,
    testid: "bento-matterport",
  },
  {
    icon: Layers,
    span: "md:col-span-5",
    title: "Survey + Scan",
    body: "The best value: precise dimensions for your architect, and a 3D scan that answers what drawings can't — ceilings, fixtures, storefront details.",
    meta: "BEST VALUE",
    image: IMAGES.mall,
    testid: "bento-combined",
  },
];

export default function Home() {
  return (
    <Page>
      <section className="blueprint-grid relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-24 pt-36 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pb-32 lg:pt-44">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <SectionTag>Field measurement · Matterport 3D · Nationwide</SectionTag>
            </motion.div>
            <h1
              data-testid="hero-headline"
              className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
            >
              <MaskedLine delay={0.25}>Before your architect</MaskedLine>
              <MaskedLine delay={0.38}>draws a line, know</MaskedLine>
              <MaskedLine delay={0.51}>
                what's <span className="text-outline">really there.</span>
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
            >
              Global Dimensions measures your retail space in person and delivers accurate
              existing-conditions drawings and 3D scans — so your design starts from facts,
              not assumptions.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/quote"
                data-testid="hero-quote-button"
                className="group flex items-center gap-2 rounded-sm bg-laser px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-canvas shadow-[0_0_32px_rgba(245,158,11,0.3)] transition-colors duration-200 hover:bg-amber-400"
              >
                Request a Quote
                <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-button"
                className="rounded-sm border border-cad/40 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-cad transition-colors duration-200 hover:border-cad hover:bg-cad/10"
              >
                Explore Services
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="mt-14 grid grid-cols-3 gap-6 border-t border-white/10 pt-6"
            >
              {[
                ["50+", "States + PR & Guam"],
                ["7–10", "Day file turnaround"],
                ["100%", "Measured in person"],
              ].map(([v, l]) => (
                <div key={l} data-testid={`hero-stat-${l.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <p className="font-display text-2xl font-bold text-cad sm:text-3xl">{v}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{l}</p>
                </div>
              ))}
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <HeroVisual />
          </div>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <Reveal>
          <SectionTag>What we deliver</SectionTag>
          <h2 className="max-w-3xl font-display text-2xl font-bold leading-tight tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            As-built surveys and Matterport scans for retail spaces.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
          {BENTO.map((card, i) => (
            <Reveal key={card.title} delay={i * 0.12} className={card.span}>
              <Link
                to="/services"
                data-testid={card.testid}
                className="group flex h-full flex-col overflow-hidden rounded-md border border-white/10 bg-surface transition-colors duration-300 hover:border-cad/40"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
                  <span className="absolute right-4 top-4 rounded-sm border border-cad/30 bg-canvas/70 px-2.5 py-1 font-mono text-[10px] tracking-[0.2em] text-cad backdrop-blur-sm">
                    {card.meta}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <div className="flex items-center gap-3">
                    <card.icon size={20} className="text-laser" />
                    <h3 className="font-display text-lg font-semibold text-slate-100 sm:text-xl">
                      {card.title}
                    </h3>
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{card.body}</p>
                  <span className="mt-5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-cad/80 transition-colors group-hover:text-cad">
                    Learn more <ArrowUpRight size={12} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
          <Reveal delay={0.36} className="md:col-span-7">
            <div
              data-testid="bento-coverage"
              className="blueprint-grid relative flex h-full flex-col justify-between overflow-hidden rounded-md border border-white/10 bg-surface p-7"
            >
              <div>
                <div className="flex items-center gap-3">
                  <MapPin size={20} className="text-laser" />
                  <h3 className="font-display text-lg font-semibold text-slate-100 sm:text-xl">
                    Wherever your next store is
                  </h3>
                </div>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400">
                  We travel to every site we survey, across all 50 states and beyond — spaces
                  as far away as Puerto Rico and Guam. If you need a site measured, we'll be there.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {TERRITORIES.map((t) => (
                  <span key={t} className="rounded-sm border border-laser/50 px-2.5 py-1 font-mono text-[10px] tracking-[0.2em] text-laser">
                    {t}
                  </span>
                ))}
                <span className="rounded-sm border border-cad/40 px-2.5 py-1 font-mono text-[10px] tracking-[0.2em] text-cad">
                  ALL 50 STATES
                </span>
                <span className="rounded-sm border border-white/15 px-2.5 py-1 font-mono text-[10px] tracking-[0.2em] text-slate-400">
                  + BEYOND
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/5 bg-surface/40">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <Reveal>
            <SectionTag>Photo → CAD</SectionTag>
            <h2 className="max-w-3xl font-display text-2xl font-bold leading-tight tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
              Drag the line. That's the job — turning a real space into drawing truth.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            <BlueprintSlider />
          </Reveal>
          <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
            Left: as-built CAD interpretation · Right: site photograph
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <Reveal>
          <SectionTag>Sample walkthrough</SectionTag>
          <h2 className="max-w-3xl font-display text-2xl font-bold leading-tight tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Don't take our word for it — walk the space yourself.
          </h2>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
            This is a real Matterport tour captured on site. Click in, look around, and move
            through the space — this is exactly what your team receives, viewable from any
            computer or phone.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12">
          <div data-testid="matterport-embed" className="relative">
            <span className="absolute -left-3 -top-3 z-10 h-6 w-6 border-l-2 border-t-2 border-cad/70" />
            <span className="absolute -bottom-3 -right-3 z-10 h-6 w-6 border-b-2 border-r-2 border-cad/70" />
            <div className="overflow-hidden rounded-sm border border-white/10">
              <iframe
                src={MATTERPORT_TOUR_URL}
                title="Sample Matterport 3D walkthrough of a surveyed space"
                className="aspect-video w-full bg-surface"
                allow="xr-spatial-tracking; fullscreen"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
          <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
            Live 3D tour · Shared by link · No site visit required to explore
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <Reveal>
          <SectionTag>Process</SectionTag>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="max-w-2xl font-display text-2xl font-bold leading-tight tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
              From request to drawings in about two weeks.
            </h2>
            <Link
              to="/process"
              data-testid="process-teaser-link"
              className="group flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-cad transition-colors hover:text-slate-100"
            >
              Full process <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-md border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
          {PROCESS_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08} className="bg-canvas">
              <div className="group h-full p-6 transition-colors duration-300 hover:bg-surface">
                <p className="font-mono text-xs tracking-[0.3em] text-laser">{s.n}</p>
                <h3 className="mt-4 font-display text-base font-semibold text-slate-100">{s.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-400">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-white/5 bg-surface/40">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <Reveal>
            <SectionTag>Coverage</SectionTag>
            <h2 className="max-w-3xl font-display text-2xl font-bold leading-tight tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
              Every state. Every site. Measured in person.
            </h2>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
              If your next location is somewhere others won't go, we will.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div data-testid="coverage-state-grid" className="mt-12 flex flex-wrap gap-2">
              {STATES.map((s) => (
                <span
                  key={s}
                  className="rounded-sm border border-white/10 px-2.5 py-1.5 font-mono text-[10px] tracking-[0.15em] text-slate-400 transition-colors duration-200 hover:border-cad/50 hover:text-cad"
                >
                  {s}
                </span>
              ))}
              {TERRITORIES.map((t) => (
                <span
                  key={t}
                  className="rounded-sm border border-laser/50 px-2.5 py-1.5 font-mono text-[10px] tracking-[0.15em] text-laser"
                >
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="blueprint-grid relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-laser">Tight deadline?</p>
            <h2 className="mt-4 max-w-3xl font-display text-3xl font-extrabold leading-[1.1] tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Tell us when you request a quote — we'll tell you what's possible.
            </h2>
            <Link
              to="/quote"
              data-testid="cta-banner-quote-button"
              className="group mt-10 inline-flex items-center gap-2 rounded-sm bg-laser px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-canvas shadow-[0_0_32px_rgba(245,158,11,0.3)] transition-colors duration-200 hover:bg-amber-400"
            >
              Request a Quote
              <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
